import { chainOrSuspend, pyNewableType, pyNone, pyNoneType, pyStr, toJs, toPy, tryCatchOrSuspend } from "@Sk";
import PyDefUtils from "PyDefUtils";
import type html2canvas from "html2canvas";
import { reloadTableSchemaForDataGrids } from "@designer/components/DesignDataGridV3";
import { cancelDrag, drop, grab, isEqualSelection, setLastHoverSelectableElement } from "@designer/event-interception";
import {
    Component,
    ComponentProperties,
    ContainerDesignInfo,
    DesignInfo,
    DesignerEventsInteraction,
    DropInfo,
    Interaction,
    LayoutProperties,
    PropertyDescription,
    PropertyValueUpdates,
    RegionInteraction,
    ToolboxSection,
    UnsetPropertyValues,
    WholeComponentInteraction,
    getComponentParent,
} from "@runtime/components/Component";
import "@runtime/extra-python-modules";
import { base64DecToStr } from "@runtime/lib/b64";
import { designerApi } from "@runtime/runner/component-designer-api";
import { customToolboxSections, jsCustomComponents } from "@runtime/runner/components-in-js/common";
import { registerSolidComponent, solidComponents } from "@runtime/runner/components-in-js/component-from-solid";
import * as _jsComponentApi from "@runtime/runner/components-in-js/public-api";
import { AppConfig } from "@runtime/runner/data";
import { setLegacyOptions } from "@runtime/runner/legacy-features";
import { anvilMod, s_parent, s_remove_from_parent } from "@runtime/runner/py-util";
import { Slot } from "@runtime/runner/python-objects";
import {
    AppIndex,
    appIndex,
    cacheCodeItemResponse,
    clearCachedCodeItems,
    setAppConfig,
    setAppIndex,
} from "./app-content";
import "./component-designer-api";
import {
    AugmentedDropZone,
    EditPath,
    SectionInfo,
    SelectedObject,
    SelectedSectionData,
    SlotInfo,
    TemplateInfo,
    editState,
    persistentEditState,
    refreshEditingForm,
    sendEditFormBounds,
    setDomNodeForSection,
    startEditingForm,
    updateDropMode,
} from "./edit-state";
import "./event-interception";
import { getLocalFormName, resetEnvironmentForApp, setDebugOrigin, setUpEnvironmentForApp } from "./python-environment";
import {
    BoundsChangeDTO,
    ComponentDropTargetDTO,
    ContainerDesignInfoDTO,
    DesignInfoDTO,
    DesignerRpcFunctions,
    DropZoneId,
    FormDidntInstantiateError,
    HoverObjectDTO,
    IdentifySelectedObjectDTO,
    InteractionDTO,
    REFRESH,
    SelectedComponentDataDTO,
    SelectedComponentSectionDataDTO,
    SelectedObjectDTO,
} from "./shared-types";
import * as windowRpc from "./windowRpc";

// consistency between designer and runtime
// @ts-ignore
window.PyDefUtils = PyDefUtils;

windowRpc.registerFn("loadApp", async (appConfig: AppConfig, appIndex: AppIndex, debugOrigin: string) => {
    setDebugOrigin(debugOrigin);
    setLegacyOptions(appConfig.runtime_options);
    await setUpEnvironmentForApp(appConfig, appIndex);
    // windowRpc.raiseEvent("appLoaded");
});

windowRpc.registerFn("updateIndex", async (appIndex: AppIndex) => {
    setAppIndex(appIndex);
});

windowRpc.registerFn("updateConfig", async (appConfig: AppConfig) => {
    setAppConfig(appConfig);
});

windowRpc.registerFn(
    "editForm",
    async (topLevelFormName: string, editPath: EditPath, initialSelection?: IdentifySelectedObjectDTO[]) => {
        // before we start editing a form we clear any cached code items
        // that way we communicate to the designer what we depend on
        // currently only happens if we change the view to stand alone view (or in pyspace when navigating to an item template)
        clearCachedCodeItems();
        return startEditingForm(topLevelFormName, editPath, true, initialSelection);
    }
);

// @ts-ignore
window.anvil = {
    call() {
        throw new Error("Cannot use legacy anvil.call() in designer");
    },
    registerSolidComponent,
    solidComponents,
    _jsComponentApi,
    _loadAppAfter: [] as Promise<any>[],
    deferLoad() {
        let r;
        // @ts-ignore
        window.anvil._loadAppAfter.push(
            new Promise((resolve, reject) => {
                r = resolve;
            })
        );
        return r;
    },
    _loadDesigner() {
        windowRpc.initialise();
    },
};

const jsPropHooks = {
    unhandledHook(obj: any) {
        if (obj instanceof (anvilMod.URLMedia as pyNewableType)) {
            return obj._url;
        }
        return undefined;
    },
};

export const getPropertyValuesOneByOne = (
    pyComponent: Component,
    propertyDescriptions: DesignInfo["propertyDescriptions"]
) => {
    return Sk.misceval.asyncToPromise(() =>
        chainOrSuspend(
            {},
            ...propertyDescriptions
                .filter(
                    ({ type }) => type !== "object" && type !== "dict" // these are guaranteed not to be useful to the IDE
                    // (and conveniently solve the problem of trying to getattr() the 'item' off a YAML-carrier component)
                )
                .map(
                    ({ name, type }) =>
                        (values: { [prop: string]: any }) =>
                            chainOrSuspend(
                                tryCatchOrSuspend(
                                    () => Sk.abstr.gattr(pyComponent, new pyStr(name), true),
                                    (e) => {
                                        reportError(e);
                                        // don't fail here else we can't select this component
                                        return null;
                                    }
                                ),
                                (value) => ({
                                    ...values,
                                    [name]: toJs(value, jsPropHooks),
                                })
                            )
                )
        )
    );
};

const setPropertyValuesOneByOne = (pyComponent: Component, updates: object) => {
    const retrievedUpdates: { [prop: string]: any } = {};

    return Sk.misceval.asyncToPromise(() =>
        chainOrSuspend(
            null,
            ...Object.entries(updates).map(
                ([name, value]) =>
                    () =>
                        Sk.abstr.sattr(pyComponent, new pyStr(name), toPy(value), true)
            ),
            ...Object.keys(updates).map(
                (name) => () =>
                    chainOrSuspend(Sk.abstr.gattr(pyComponent, new pyStr(name), true), (pyVal) => {
                        retrievedUpdates[name] = toJs(pyVal, jsPropHooks);
                    })
            ),
            () => retrievedUpdates
        )
    );
};

let resendBoundsTimeout: any = null;
let unsetPropertyValuesTimeout: any = null;

export const setComponentPropertyValues = async (componentName: string, updates: { [prop: string]: any }) => {
    const component = editState.componentsByName.get(componentName);
    if (component) {
        const retrievedUpdates = await setPropertyValuesOneByOne(component, updates);

        // TODO: We could short-circuit this if no properties were set to null or empty string, or something.
        clearTimeout(unsetPropertyValuesTimeout);
        // do this in a setTimeout so that react components can render
        unsetPropertyValuesTimeout = setTimeout(() => {
            const unsetPropertyValues = component.anvil$hooks.getUnsetPropertyValues?.();
            if (unsetPropertyValues) {
                windowRpc.raiseEvent("componentUnsetPropertyValuesChanged", componentName, unsetPropertyValues);
            }
        }, 20);

        clearTimeout(resendBoundsTimeout);
        resendBoundsTimeout = setTimeout(() => {
            resendSelectedObjectBounds();
            resendSelectedObjectInteractions();
        }, 100);
        return retrievedUpdates;
    } else {
        console.error(`Could not set properties for component '${componentName}: Component not found.`);
        return {};
    }
};

windowRpc.registerFn("setComponentPropertyValues", setComponentPropertyValues);

export const setSectionPropertyValues = async (componentName: string, id: string, updates: { [prop: string]: any }) => {
    const component = editState.componentsByName.get(componentName);
    if (component) {
        const hookFn = component.anvil$hooks.setSectionPropertyValues;
        const retrievedUpdates = await Sk.misceval.asyncToPromise(() => hookFn?.(id, updates) || {});
        if (retrievedUpdates === true) {
            return component.anvil$hooks.getSections?.()!.find((s) => s.id === id)!.propertyValues;
        } else {
            return retrievedUpdates;
        }
    } else {
        console.error(`Could not set properties for component '${componentName}: Component not found.`);
        return {};
    }
};

windowRpc.registerFn("setSectionPropertyValues", setSectionPropertyValues);

windowRpc.registerFn("setSlotTemplatePropertyValues", async (slotName: string, updates: { [prop: string]: any }) => {
    const slot = editState.slotsByName.get(slotName);
    if (slot) {
        return await setPropertyValuesOneByOne(slot.template!, updates);
    } else {
        console.error(`Could not set properties for slot '${slotName}: Slot not found.`);
        return {};
    }
});

const setLayoutProperties = async (component: Component, optimisticUpdates: LayoutProperties) => {
    const parent = getComponentParent(component);
    if (parent === pyNone) {
        throw new Error(`Can't set layout properties on top level form`);
    }
    let updates: PropertyValueUpdates | null | undefined;
    if (parent.anvil$hooks.updateLayoutProperties) {
        // undefined/null or a dictionary of new updates
        updates = await Sk.misceval.asyncToPromise(() =>
            parent.anvil$hooks.updateLayoutProperties(component, optimisticUpdates)
        );
        const current = editState.layoutPropertyInfoByComponent.get(component);
        if (current) {
            editState.layoutPropertyInfoByComponent.set(component, {
                ...current,
                layoutProperties: { ...current.layoutProperties, ...optimisticUpdates, ...updates },
            });
        }
        // possibly overkill - but we might have changed position during layout property updates
        resendSelectedObjectBounds();
    } else {
        refreshEditingForm(); // No await, deliberately.
    }
    return updates;
};

windowRpc.registerFn("setComponentLayoutProperties", (componentName, updates) => {
    const component = editState.componentsByName.get(componentName);
    if (!component) {
        throw new Error(`No such component ${componentName}`);
    }
    return setLayoutProperties(component, updates);
});

windowRpc.registerFn("setSlotLayoutProperties", (slotName, updates) => {
    const slot = editState.slotsByName.get(slotName);
    if (!slot) {
        throw new Error(`No such slot ${slotName}`);
    }
    return setLayoutProperties(slot.template || slot.placeholder, updates);
});

const deleteObjects = async (objects: SelectedObjectDTO[]) => {
    // We tried jumping selection to nearest common ancestor of selected objects. It wasn't a great UX.
    // if (persistentEditState.selection.length > 0) {
    //     // Select the nearest common ancestor of selected components.
    //     // Assumes that the objects being deleted are selected, which isn't necessarily going to be the case forever.
    //     const allAncestors: Component[][] = [];
    //     for (const {type, data} of objects) {
    //         if (type === "component") {
    //             const component = editState.componentsByName.get(data.name)!
    //             const ancestors = editState.ancestorsByComponent.get(component)!;
    //             allAncestors.push(ancestors);
    //         } else if (type === "section") {
    //             const component = editState.componentsByName.get(data.name)!
    //             const ancestors = editState.ancestorsByComponent.get(component)!;
    //             allAncestors.push([component, ...ancestors]);
    //         }
    //     }
    //
    //     if (allAncestors.length === 0) {
    //         await deselectAll();
    //     } else {
    //         let commonAncestor = null;
    //         for (let i = 0; ; i++) {
    //             let currentAncestor = allAncestors[0][allAncestors[0].length - 1 - i];
    //             let match = !!currentAncestor;
    //             for (const ancestors of allAncestors) {
    //                 if (ancestors[ancestors.length - 1 - i] !== currentAncestor) {
    //                     match = false;
    //                     break;
    //                 }
    //             }
    //             if (!match) {
    //                 break;
    //             }
    //             commonAncestor = currentAncestor;
    //         }
    //
    //         if (commonAncestor) {
    //             await select({type: "component", data: {name: editState.namesByComponent.get(commonAncestor)!}});
    //         }
    //     }
    // }

    await deselectAll();

    const orphanedComponents = editState.orphanedComponents;
    const orphanedSlots = editState.orphanedSlots;
    let notifyOrphanedObjects = false;

    const updateOrphaned = (orphaned: string[], name: string) => {
        const index = orphaned.indexOf(name);
        if (index === -1) return;
        orphaned.splice(index, 1);
        notifyOrphanedObjects = true;
    };

    for (const { type, data } of objects) {
        switch (type) {
            case "component":
                const remove = editState.componentsByName.get(data.name)!.tp$getattr(s_remove_from_parent)!;
                // TODO - cleanup sections and componentsByName
                await Sk.misceval.asyncToPromise(() => Sk.misceval.callsimOrSuspendArray(remove));
                updateOrphaned(orphanedComponents, data.name);
                // TODO: If we removed from a slot, put the template back if necessary.
                break;
            case "slot":
                // TODO: Remove slots sensibly.
                // updateOrphaned(orphanedSlots, data.name);
                await refreshEditingForm();
                break;
            case "section":
                setSectionPropertyValues(data.name, data.id, {});
                break;
        }
    }
    if (notifyOrphanedObjects) {
        windowRpc.raiseEvent("notifyOrphanedObjects", orphanedComponents, orphanedSlots);
    }
};

windowRpc.registerFn("deleteObjects", deleteObjects);

const renameComponent = async (oldName: string, newName: string) => {
    const wasSelected = persistentEditState.selection.find((s) => s.type === "component" && s.data.name === oldName);
    await refreshEditingForm(wasSelected ? [{ type: "component", data: { name: newName } }] : undefined);
};

windowRpc.registerFn("renameComponent", renameComponent);

const renameSlot = async (oldName: string, newName: string) => {
    const wasSelected = persistentEditState.selection.find((s) => s.type === "slot" && s.data.name === oldName);
    await refreshEditingForm(wasSelected ? [{ type: "slot", data: { name: newName } }] : undefined);
};

windowRpc.registerFn("renameSlot", renameSlot);

windowRpc.registerFn("refreshEditingForm", async (refreshType = REFRESH.DEFAULT) => {
    if (refreshType === REFRESH.HARD) {
        document.location.reload();
    } else if (refreshType === REFRESH.ENV) {
        await resetEnvironmentForApp();
        await refreshEditingForm();
    } else {
        await refreshEditingForm();
    }
});

windowRpc.registerFn("setCreatingObject", async (obj) => {
    if (persistentEditState.creating !== obj) {
        // This really only stops setting to null repeatedly, possibly while form update is still happening.
        persistentEditState.creating = obj;
        await updateDropMode();
    }
});

export const finishPaste = async (dropTarget: ComponentDropTargetDTO, dropInfo: DropInfo) => {
    if (persistentEditState.pasting) {
        windowRpc.raiseEvent("pasteObjects", persistentEditState.pasting, dropTarget, dropInfo);
    }
    persistentEditState.pasting = null;
    await updateDropMode();
};
export const cancelPaste = async () => {
    persistentEditState.pasting = null;
    await updateDropMode();
};

windowRpc.registerFn("setPastingObjects", async (objects, immediate) => {
    persistentEditState.pasting = objects;
    await updateDropMode();
    const selectedObject = persistentEditState.selection[0];

    if (immediate) {
        // Pick a suitable dropzone, create the objects to be pasted there.
        let dz: AugmentedDropZone | null = null;
        if (selectedObject) {
            switch (selectedObject.type) {
                case "component":
                    // Look for suitable dropzones in this component, working upwards through parents until we find one.
                    let component: Component | pyNoneType = editState.componentsByName.get(selectedObject.data.name)!;
                    while (component && component !== pyNone && !dz) {
                        const componentDzs = editState.dropZonesByComponent.get(component as Component);
                        if (componentDzs && componentDzs.length > 0) {
                            // TODO: Choose the DZ marked "defaultDropZone: true" if there is one, otherwise just take the last one:
                            dz = componentDzs[componentDzs.length - 1];
                        } else {
                            component = component.tp$getattr(s_parent);
                        }
                    }
                    if (!dz) {
                        // we didn't find a drpozone from walking component parents, so see if we are a layout with slots
                        const slotsDropZones = [...editState.dropZonesBySlot.values()];
                        // TODO: Choose the DZ marked "defaultDropZone: true" if there is one, otherwise just take the last one:
                        dz = slotsDropZones[0]?.at(-1) ?? null;
                    }
                    break;
                case "slot":
                    // You can't paste into a selected slot
                    break;
                case "section":
                    // Can't paste into sections for now.
                    break;
            }
        }

        if (dz) {
            const dropTarget: ComponentDropTargetDTO = { type: dz.dropTarget.type, name: dz.dropTarget.name };
            await finishPaste(dropTarget, dz.dropInfo ?? dz.freePlacement!([{ x: 0, y: 0 }]).dropInfo);
        } else {
            console.log("Not pasting - no suitable dropzone found.");
            await cancelPaste();
        }
    }
});

windowRpc.registerFn("getCustomComponents", () => {
    const toolboxSections: ToolboxSection[] = [];

    for (const section of customToolboxSections) {
        toolboxSections.push(section);
    }

    const customComponents = Object.values(jsCustomComponents).map(({ spec }) => spec);

    // TODO: Do we need to send layouts separately from customComponents, which should declare whether they are layouts anyway?
    // const layouts: CustomLayoutYaml[] = [];

    // layouts.push({
    //     name: "My.Custom.LayoutForm",
    //     layout: {type: 'form:DEP_ID:My.Custom.LayoutForm'},
    // })

    return { toolboxSections, customComponents };
});

let interactionCallbacksById: { [id: string]: any } = {};
let nextInteractionId = 1;

windowRpc.registerFn("componentInteraction", (callbackId: string, fnName: string, ...args: any[]) =>
    interactionCallbacksById[callbackId][fnName]?.(...args)
);

function maybeAddDefaultCustomComponentInteraction(pyComponent: Component, interactions: Interaction[]) {
    // If the component is a custom component from this project, insert a
    // "switch to this form" whole-component interaction
    if (interactions.length === 0) {
        if (getLocalFormName(pyComponent) && editState.namesByComponent.get(pyComponent) !== "") {
            interactions = interactions.concat([
                {
                    type: "whole_component",
                    title: "Edit custom component",
                    icon: "edit_form",
                    // default: true, // not for now; previous behaviour was "jump to first event handler"
                    callbacks: {
                        execute() {
                            designerApi.startEditingSubform(pyComponent);
                        },
                    },
                },
            ]);
        }
    }
    return interactions;
}

export function getInteractions(pyComponent: Component): Interaction[] {
    const interactions = pyComponent.anvil$hooks.getInteractions?.() ?? [];
    return maybeAddDefaultCustomComponentInteraction(pyComponent, interactions);
}

export function getUnsetPropertyValues(pyComponent: Component): UnsetPropertyValues {
    return pyComponent.anvil$hooks.getUnsetPropertyValues?.() ?? {};
}

export async function getDesignInfo(
    pyComponent: Component
): Promise<DesignInfo & { propertyValues: ComponentProperties }> {
    if (!editState.editingForm) {
        throw new FormDidntInstantiateError();
    }
    // const editingFormComponent = editState.editingForm.pyComponent;
    // const thisIsTheLayout = editingFormComponent instanceof WithLayout && pyComponent === editingFormComponent._withLayout.pyLayout;
    const properties = pyComponent.anvil$hooks.properties;
    let events = pyComponent.anvil$hooks.events;
    if (typeof events === "object" && !Array.isArray(events)) {
        // Backward Compatibility
        events = Object.values(events);
    }

    return {
        propertyDescriptions: properties,
        events,
        interactions: getInteractions(pyComponent),
        propertyValues: await getPropertyValuesOneByOne(pyComponent, properties),
        unsetPropertyValues: getUnsetPropertyValues(pyComponent),
    };
}

const getInteractionsForTransfer = (interactions?: Interaction[]) =>
    (interactions || [])
        .map(({ callbacks, ...interaction }) => {
            const id = "" + nextInteractionId++;
            interactionCallbacksById[id] = callbacks;
            if (interaction.type === "region") {
                if (interaction.bounds instanceof HTMLElement) {
                    interaction.bounds = interaction.bounds.getBoundingClientRect();
                }
                // don't send region interactions that don't have bounds
                if (!interaction.bounds) return false;
                // fallthrough
            }
            return { ...interaction, id } as InteractionDTO;
        })
        .filter(Boolean);

export const getDesignInfoForTransfer = ({
    propertyDescriptions,
    events,
    interactions,
    unsetPropertyValues,
}: DesignInfo): DesignInfoDTO => {
    return {
        propertyDescriptions: propertyDescriptions as any, // the IDE has an overly-cleverly-typed system for describing these that doesn't play with ours, but it's the same actual data
        events,
        interactions: getInteractionsForTransfer(interactions),
        unsetPropertyValues,
    };
};

const getContainerDesignInfoForTransfer = (
    { layoutPropertyDescriptions, interactions }: ContainerDesignInfo,
    layoutPropertyValues: LayoutProperties
): ContainerDesignInfoDTO => ({
    layoutPropertyDescriptions: layoutPropertyDescriptions as any, // as propertyDescriptions above
    layoutPropertyValues,
    interactions: getInteractionsForTransfer(interactions),
});

const getDesignInfoForContainer = (pyContainer: Component | pyNoneType, pyComponent: Component) =>
    pyContainer === pyNone
        ? undefined
        : pyContainer.anvil$hooks?.getContainerDesignInfo?.(pyComponent) || { layoutPropertyDescriptions: [] };

const getDesignInfoForSlot = (slotName: string, pyComponent: Component) => {
    const slot: Slot | undefined = editState.editingForm?.pyComponent.anvil$yamlCarrier!.pyLayoutSlots?.quick$lookup(
        new pyStr(slotName)
    );
    return slot?._slotState.getContainerDesignInfo(pyComponent) || { layoutPropertyDescriptions: [] };
};

let sendBounds = true;
let selectedObjectObserverId: ReturnType<typeof setTimeout>;

const selectedObjectObserverCallback = () => {
    if (!sendBounds) return;
    clearTimeout(selectedObjectObserverId);
    selectedObjectObserverId = setTimeout(() => {
        resendSelectedObjectBounds();
        resendSelectedObjectInteractions();
    }, 10);
};

const _selectedObjectResizeObserver = new ResizeObserver(selectedObjectObserverCallback);
const _selectedObjectIntersectionObserver = new IntersectionObserver(selectedObjectObserverCallback, {
    threshold: [0, 0.05, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 0.95, 1],
});

const selectedObjectObserver = {
    disconnect() {
        _selectedObjectIntersectionObserver.disconnect();
        _selectedObjectResizeObserver.disconnect();
    },
    observe(element?: Element | null) {
        if (!element) return;
        _selectedObjectIntersectionObserver.observe(element);
        _selectedObjectResizeObserver.observe(element);
    },
};

const defaultBoundingRect = { bottom: 0, height: 0, left: 0, right: 0, top: 0, width: 0, x: 0, y: 0 };

let previousSelection: SelectedObjectDTO[] = [];
let previousSelectionAncestors: Set<Component> = new Set();
export async function notifySelectionChange() {
    const previousInteractionCallbacksById = interactionCallbacksById;
    interactionCallbacksById = {}; // Clear previous interactions

    const selectionAncestors: Set<Component> = new Set();
    sendBounds = false;
    selectedObjectObserver.disconnect();

    const selection = (
        await Promise.all(
            persistentEditState.selection.map(async (selectedObject) => {
                const { type, data } = selectedObject;

                switch (type) {
                    case "component": {
                        const { name } = data;
                        const pyComponent = editState.componentsByName.get(name)!;
                        const designInfo = await getDesignInfo(pyComponent);
                        const layoutInfo = editState.layoutPropertyInfoByComponent.get(pyComponent); // Will be undefined for top-level form
                        const layoutPropertyValues =
                            editState.layoutPropertyInfoByComponent.get(pyComponent)?.layoutProperties || {};
                        const containerDesignInfo: ContainerDesignInfo | undefined = layoutInfo?.targetSlot
                            ? getDesignInfoForSlot(layoutInfo.targetSlot, pyComponent)
                            : getDesignInfoForContainer(getComponentParent(pyComponent), pyComponent);

                        selectionAncestors.add(pyComponent); // Ancestors include self
                        let i = 1;
                        for (const ancestor of editState.ancestorsByComponent.get(pyComponent)!) {
                            selectionAncestors.add(ancestor);
                        }

                        const domElement = pyComponent.anvil$hooks.domElement;
                        selectedObjectObserver.observe(domElement);

                        return {
                            type,
                            data: {
                                name,
                                type: editState.componentTypes.get(pyComponent),
                                bounds: domElement?.getBoundingClientRect() ?? defaultBoundingRect,
                                propertyValues: designInfo.propertyValues,
                                designInfo: getDesignInfoForTransfer(designInfo),
                                containerDesignInfo:
                                    containerDesignInfo &&
                                    getContainerDesignInfoForTransfer(containerDesignInfo, layoutPropertyValues),
                            } as SelectedComponentDataDTO,
                        };
                    }

                    case "section": {
                        const { name, id } = data;
                        const pyComponent = editState.componentsByName.get(name)!;
                        const section = (pyComponent.anvil$hooks.getSections?.() || []).find((s) => s.id === id);
                        if (!section) {
                            console.warn(
                                "Component '",
                                name,
                                "' advertised section '",
                                id,
                                "' but now it's not there any more. Bad component, no cookie. Component object:",
                                pyComponent
                            );
                            return null;
                        }

                        selectionAncestors.add(pyComponent); // Ancestors include self
                        let i = 1;
                        for (const ancestor of editState.ancestorsByComponent.get(pyComponent)!) {
                            selectionAncestors.add(ancestor);
                        }

                        let { title, propertyDescriptions, propertyValues, interactions, element } = section;
                        setDomNodeForSection(name, id, element); // belt and braces, but while we've got it...

                        selectedObjectObserver.observe(element);

                        return {
                            type,
                            data: {
                                name,
                                id,
                                title,
                                bounds: element.getBoundingClientRect(), // not null
                                propertyValues,
                                designInfo: getDesignInfoForTransfer({
                                    propertyDescriptions,
                                    events: [],
                                    interactions,
                                    unsetPropertyValues: {},
                                }),
                            } as SelectedComponentSectionDataDTO,
                        };
                    }

                    case "slot": {
                        const { name, slot, placeholder, template } = data;
                        const templateDesignInfo = template && (await getDesignInfo(template));

                        if (slot) {
                            // It actually exists on the page (isn't an orphan)

                            const containerDesignInfo = slot._slotState.cache?.hooks.getContainerDesignInfo?.(
                                template || placeholder
                            ) || { layoutPropertyDescriptions: [] };
                            const layoutPropertyValues = toJs(slot._slotState.pyLayoutProps) as LayoutProperties;
                            const domElement = (template || placeholder).anvil$hooks.domElement;
                            selectedObjectObserver.observe(domElement);

                            return {
                                type,
                                data: {
                                    name,
                                    bounds: domElement?.getBoundingClientRect() ?? defaultBoundingRect,
                                    templateComponent: template
                                        ? {
                                              propertyValues: templateDesignInfo!.propertyValues,
                                              designInfo: getDesignInfoForTransfer(templateDesignInfo!),
                                          }
                                        : undefined,
                                    oneComponent: slot._slotState.oneComponent,
                                    containerDesignInfo: getContainerDesignInfoForTransfer(
                                        containerDesignInfo,
                                        layoutPropertyValues
                                    ),
                                },
                            };
                        } else {
                            // This is an orphaned slot
                            return {
                                type,
                                data: {
                                    name,
                                    bounds: new DOMRect(),
                                },
                            };
                        }
                    }
                    default:
                        console.warn(`Trying to transfer a selected object of type '${type}'`);
                        return selectedObject;
                }
            })
        )
    ).filter(Boolean) as SelectedObjectDTO[];

    for (const a of [...selectionAncestors].filter((sa) => !previousSelectionAncestors.has(sa))) {
        const interactions = getInteractions(a);
        const designerEventsInteraction = interactions.find(({ type }) => type === "designer_events") as
            | DesignerEventsInteraction
            | undefined;
        designerEventsInteraction?.callbacks.onSelectDescendent?.();
    }

    for (const a of [...previousSelectionAncestors].filter((pa) => !selectionAncestors.has(pa))) {
        const interactions = getInteractions(a);
        const designerEventsInteraction = interactions.find(({ type }) => type === "designer_events") as
            | DesignerEventsInteraction
            | undefined;
        designerEventsInteraction?.callbacks.onDeselectDescendent?.();
    }
    previousSelectionAncestors = selectionAncestors;

    for (const s of selection) {
        if (s.type === "component" || s.type === "section") {
            //@ts-ignore
            const wasSelected = previousSelection.find(
                ({ type, data: { name, id } }) => type === s.type && name === s.data.name && id === s.data.id
            );
            if (!wasSelected) {
                const designerEventsInteractionId = s.data.designInfo.interactions.find(
                    ({ type }) => type === "designer_events"
                )?.id;
                if (designerEventsInteractionId) {
                    interactionCallbacksById[designerEventsInteractionId].onSelect?.();
                }
            }
        }
    }
    for (const s of previousSelection) {
        if (s.type === "component" || s.type === "section") {
            //@ts-ignore
            const isNowSelected = selection.find(
                ({ type, data: { name, id } }) => type === s.type && name === s.data.name && id === s.data.id
            );
            if (!isNowSelected) {
                const designerEventsInteractionId = s.data.designInfo.interactions.find(
                    ({ type }) => type === "designer_events"
                )?.id;
                if (designerEventsInteractionId) {
                    previousInteractionCallbacksById[designerEventsInteractionId]?.onDeselect?.();
                }
            }
        }
    }
    if (selectionAncestors.size > 0) {
        // For all components not selected (and without selected descendents), call onSelectOther
        for (const c of editState.namesByComponent.keys()) {
            if (!selectionAncestors.has(c)) {
                const designerEventsInteraction = c.anvil$hooks
                    .getInteractions?.()
                    .find(({ type }) => type === "designer_events") as DesignerEventsInteraction;
                designerEventsInteraction?.callbacks.onSelectOther?.();
            }
        }
    }

    previousSelection = selection;
    setTimeout(() => {
        // want to avoid the resizeObserver sending bounds immediately to the designer
        // so delay setting this flag to true
        sendBounds = true;
    });
    await windowRpc.call("select", selection);
}

let currentHoverData: HoverObjectDTO | null = null;
let currentHoverEl: HTMLElement | null = null;

function resendHoverBounds() {
    if (!currentHoverData) return;
    if (currentHoverEl) {
        currentHoverData.bounds = currentHoverEl.getBoundingClientRect();
    }
    windowRpc.raiseEvent("objectHover", currentHoverData);
}

const hoverResizeObserver = new ResizeObserver(resendHoverBounds);

export function notifyHoverObject(hoverData: HoverObjectDTO | null) {
    hoverResizeObserver.disconnect();

    currentHoverEl = null;
    currentHoverData = null;

    if (hoverData === null) return windowRpc.raiseEvent("objectHover", null);

    let { type, name, bounds } = hoverData;
    switch (type) {
        case "component":
            const pyComponent = editState.componentsByName.get(name);
            // might be undefined after a refresh of the editing form
            if (!pyComponent) return;
            const domElement = pyComponent.anvil$hooks.domElement!;
            bounds ??= domElement.getBoundingClientRect();
            currentHoverData = { type, name, bounds };
            currentHoverEl = domElement;
            hoverResizeObserver.observe(domElement);
            break;
        case "slot":
        case "section":
        case "designInteraction":
            break;
        default:
            type = "component";
            name = "";
            break;
    }

    windowRpc.raiseEvent("objectHover", { type, name, bounds });
}

windowRpc.registerFn("hover", (object: IdentifySelectedObjectDTO | null) => {
    let bounds: DOMRect | undefined,
        name = "";
    if (object?.type === "component") {
        name = object.data.name;
        bounds = editState.componentsByName.get(object.data.name)?.anvil$hooks.domElement?.getBoundingClientRect();
    } else if (object?.type === "section") {
        bounds = editState.componentsByName
            .get(object.data.name)
            ?.anvil$hooks.getSectionDomElement?.(object.data.id)
            ?.getBoundingClientRect();
    } else if (object?.type === "slot") {
        bounds = editState.slotsByName.get(object.data.name)?.element.getBoundingClientRect();
    }
    notifyHoverObject(object && bounds ? { type: object.type, name, bounds } : (null as any));
});

const getDzById = (dzId: DropZoneId | null) => {
    let dz: AugmentedDropZone | null = null;
    for (const tdz of editState.dropZones.values()) {
        if (tdz.id === dzId) {
            dz = tdz;
            break;
        }
    }
    return dz;
};

windowRpc.registerFn("hoverDropZone", (dzId) => {
    const dz = getDzById(dzId);
    if (dz) {
        const { id, originalBounds, dropTarget } = dz;
        windowRpc.raiseEvent("updateDropZones", [
            {
                id,
                originalBounds,
                bounds: originalBounds,
                targetBounds: dropTarget.getBounds(),
                isCurrent: true,
                occlusions: [],
            },
        ]);
    } else {
        windowRpc.raiseEvent("updateDropZones", []);
    }
});

windowRpc.registerFn("completeDrag", (dzId) => {
    const dz = getDzById(dzId);
    if (dz) {
        drop(dz);
    }
});

windowRpc.registerFn("dropCreatingObject", (dzId, createAnother) => {
    const dz = getDzById(dzId);
    if (dz && persistentEditState.creating) {
        windowRpc.raiseEvent(
            "createObject",
            { type: dz.dropTarget.type, name: dz.dropTarget.name },
            persistentEditState.creating,
            dz.dropInfo ?? {},
            !createAnother
        );
        windowRpc.raiseEvent("updateDropZones", []);
    }
});

export function resendObjectBounds(updates: BoundsChangeDTO[]) {
    windowRpc.raiseEvent("objectBoundsChanged", updates);
}

export function resendSelectedObjectBounds() {
    if (!editState?.editingForm) return;
    // Only set once we're initialised
    if (editState.dragState) {
        // The objects we're dragging must be selected, so just send those instead. This mostly makes scrolling nicer.
        resendObjectBounds(
            editState.dragState.objects.map(
                ({
                    object: {
                        type,
                        data: { name },
                    },
                    dummyElement,
                }) => {
                    return { type, name, bounds: dummyElement.getBoundingClientRect() };
                }
            )
        );
    } else {
        resendObjectBounds(
            persistentEditState.selection
                .map(({ type, data }) => {
                    if (type === "slot") {
                        const slot = editState.slotsByName.get(data.name);
                        if (!slot) return null;
                        return { type, name: data.name, bounds: slot.element.getBoundingClientRect() };
                    }
                    const name = data.name;
                    const component = editState.componentsByName.get(name);
                    if (!component) {
                        return null;
                    }
                    if (type === "section") {
                        const id = (data as SelectedSectionData).id;
                        const bounds = component.anvil$hooks.getSectionDomElement?.(id)?.getBoundingClientRect();
                        return { type, name, bounds, id };
                    } else {
                        const bounds = component.anvil$hooks.domElement!.getBoundingClientRect();
                        return { type, name, bounds };
                    }
                })
                .filter(Boolean)
        );
    }
}

let objectInteractionsTimeout: ReturnType<typeof setTimeout> | undefined = undefined;

export function resendSelectedObjectInteractions() {
    clearTimeout(objectInteractionsTimeout);

    objectInteractionsTimeout = setTimeout(() => {
        windowRpc.raiseEvent(
            "componentInteractionsChanged",
            persistentEditState.selection
                .map(({ type, data: { name } }) => {
                    const pyComponent = editState.componentsByName.get(name);
                    let interactions = type === "component" && pyComponent?.anvil$hooks.getInteractions?.();
                    if (type === "component" && interactions) {
                        interactions = pyComponent
                            ? maybeAddDefaultCustomComponentInteraction(pyComponent, interactions)
                            : [];
                        return {
                            type,
                            name,
                            interactions: getInteractionsForTransfer(interactions),
                        };
                    } else {
                        return null;
                    }
                })
                .filter(Boolean)
        );
    }, 10);
}

export async function deselectAll() {
    persistentEditState.selection = [];
    await notifySelectionChange();
}

export async function select(object: SelectedObject, append = false) {
    if (
        (persistentEditState.selection.length === 1 || append) &&
        persistentEditState.selection.find((so) => isEqualSelection(so, object))
    ) {
        return;
    }
    if (!append) {
        persistentEditState.selection = [];
    }
    persistentEditState.selection.push(object);

    await notifySelectionChange();
}

export const inflateSelectedObjectDTO = (object: IdentifySelectedObjectDTO) =>
    object.type === "slot" ? { ...object, data: editState!.slotsByName.get(object.data.name)! } : object;

windowRpc.registerFn("select", async (object, append = false) => {
    const inflatedObject: SelectedObject = inflateSelectedObjectDTO(object);
    await select(inflatedObject, append);
});

export const deselect = async (so: IdentifySelectedObjectDTO) => {
    console.log("Deselecting:", so, "from", persistentEditState.selection);
    persistentEditState.selection = persistentEditState.selection.filter((v) => !isEqualSelection(v, so));
    await notifySelectionChange();
};

windowRpc.registerFn("deselect", deselect);

windowRpc.registerFn("deselectAll", deselectAll);

windowRpc.registerFn("getDesignInfo", async (componentName) => {
    const pyComponent = editState.componentsByName.get(componentName);
    if (pyComponent) {
        return getDesignInfoForTransfer(await getDesignInfo(pyComponent));
    }
});

windowRpc.registerFn("getProperties", async (componentNames) => {
    const propDescriptions: ReturnType<DesignerRpcFunctions["getProperties"]> = {};
    for (const name of componentNames) {
        const pyComponent = editState.componentsByName.get(name);
        if (pyComponent) {
            propDescriptions[name] = (await getDesignInfo(pyComponent)).propertyDescriptions as PropertyDescription[];
        }
    }
    return propDescriptions;
});

export function createFromTemplate(templateInfo: TemplateInfo) {
    if (templateInfo.targetType === "slot") {
        windowRpc.raiseEvent(
            "createObject",
            { type: "slot", name: templateInfo.slotName },
            {
                type: "component",
                toolboxItem: templateInfo.template,
            },
            {},
            true
        );
    }
}

export function validateSelection() {
    persistentEditState.selection = persistentEditState.selection
        .filter(({ type, data }) => {
            switch (type) {
                case "component":
                    return editState.componentsByName.get(data?.name);
                case "slot":
                    return editState.slotsByName.get(data?.name);
                default:
                    console.warn(`Cannot validate selected object of type '${type}'`);
            }
        })
        .map((obj) => {
            if (obj.type === "slot") {
                return { type: "slot", data: editState.slotsByName.get(obj.data.name)! };
            } else {
                return obj;
            }
        });
}

function findRegionInteraction(interactions: Interaction[], e: MouseEvent, minSensitivity: number) {
    return interactions.find((i) => {
        if (i.type === "region" && i.sensitivity >= minSensitivity) {
            let bounds = i.bounds;
            if (bounds instanceof HTMLElement) {
                bounds = bounds.getBoundingClientRect();
            }
            if (!bounds) return false;
            return (
                bounds.x < e.clientX && bounds.y < e.clientY && bounds.right > e.clientX && bounds.bottom > e.clientY
            );
        }
    }) as RegionInteraction | undefined;
}

export async function executeRegionInteraction(
    componentName: string,
    pyComponent: Component,
    e: MouseEvent,
    minSensitivity: number,
    sectionInfo?: SectionInfo
) {
    const executeComponentRegionInteraction = async () => {
        const interactions = getInteractions(pyComponent);
        const regionInteraction = findRegionInteraction(interactions, e, minSensitivity);
        if (regionInteraction) {
            regionInteraction.callbacks.execute?.();
        } else if (minSensitivity === 0) {
            const defaultInteraction = interactions.find((i) => i.type === "whole_component" && i.default === true) as
                | WholeComponentInteraction
                | undefined;
            defaultInteraction?.callbacks.execute();
        }
    };

    if (sectionInfo) {
        const section = (pyComponent.anvil$hooks.getSections?.() || []).find(
            (section) => section.id == sectionInfo.id
        )!;
        const regionInteraction = findRegionInteraction(section.interactions || [], e, minSensitivity);
        if (regionInteraction) {
            regionInteraction.callbacks.execute?.();
        } else if (minSensitivity === 0) {
            const defaultInteraction = section.interactions.find(
                (i) => i.type === "whole_component" && i.default === true
            ) as WholeComponentInteraction | undefined;
            if (defaultInteraction) {
                defaultInteraction?.callbacks.execute();
            } else {
                await executeComponentRegionInteraction();
            }
        } else {
            await executeComponentRegionInteraction();
        }
    } else {
        await executeComponentRegionInteraction();
    }
}

let oldHeight: any = null;
let oldWidth: any = null;
function reportHeightIfChanged(): void {
    const newHeight = document.scrollingElement?.scrollHeight;
    if (newHeight !== oldHeight) {
        oldHeight = newHeight;
        windowRpc.raiseEvent("newHeight", newHeight!);
    }
    const newWidth = document.scrollingElement?.scrollWidth;
    if (newWidth !== oldWidth) {
        oldWidth = newWidth;
        resendSelectedObjectBounds();
        sendEditFormBounds();
    }
}

windowRpc.registerFn("heightChanged", reportHeightIfChanged);

let reportHeightTimeout: ReturnType<typeof setTimeout>;
let isCircuitBroken = false;
let numHeightChangesSinceLastCheck = 0;
let lastCheckedCircuitBreak = Date.now();
const CIRCUIT_BREAK_MAX_INTERVAL = 300;
const MAX_HEIGHT_CHANGES = 5;

const heightObserver = new ResizeObserver((_entries) => {
    if (isCircuitBroken) return;
    clearTimeout(reportHeightTimeout);
    reportHeightTimeout = setTimeout(() => {
        if (numHeightChangesSinceLastCheck > MAX_HEIGHT_CHANGES) {
            if (Date.now() - lastCheckedCircuitBreak < CIRCUIT_BREAK_MAX_INTERVAL) {
                isCircuitBroken = true;
            }
            numHeightChangesSinceLastCheck = 0;
            lastCheckedCircuitBreak = Date.now();
        }
        numHeightChangesSinceLastCheck++;
        reportHeightIfChanged();
    }, 10);
});

document.addEventListener("DOMContentLoaded", function () {
    heightObserver.observe(document.body);
});

document.addEventListener(
    "scroll",
    function () {
        resendSelectedObjectBounds();
        notifyHoverObject(null);
        setLastHoverSelectableElement(null);
        windowRpc.raiseEvent("updateDropZones", []);
        sendEditFormBounds();
    },
    { passive: true }
);

windowRpc.registerFn("cleanupLayoutProperties", (componentName) => {
    return editState.componentsByName.get(componentName)?.anvil$hooks.cleanupLayoutProperties?.();
});

windowRpc.registerFn("notifyAddForm", (form) => {
    appIndex.forms.push(form);
});

export let propagateMouseEvents = false;

windowRpc.registerFn("setPropagateMouseEvents", (propagate: boolean) => {
    propagateMouseEvents = !!propagate;
});

windowRpc.registerFn("setHighlight", (highlight: boolean) => {
    window.document.body.classList.toggle("anvil-highlight", highlight);
});

windowRpc.registerFn("provideCodeItem", (type, moduleName, depId, item) => {
    if (type === "asset" && item && "content" in item) {
        const content = base64DecToStr(item.content);
        // @ts-ignore
        window.anvilThemeHtml[moduleName] = content;
        console.log("Received updated asset:", moduleName);
    }
    cacheCodeItemResponse(type, moduleName, depId, item);
});

windowRpc.registerFn("beginDragObjects", (objects, { x, y }) => {
    grab(objects === "selection" ? persistentEditState.selection : objects, x, y, "mouse");
});

windowRpc.registerFn("cancelDrag", () => {
    cancelDrag();
});

windowRpc.registerFn("invalidateRecordSchemas", async () => {
    // This is a single-purpose hack. When we ship proper autocrud/record-schemas, we can generalise this to
    // invalidating the shared record-schema cache
    await reloadTableSchemaForDataGrids();
    await notifySelectionChange();
});

function doLazyImport(source: string) {
    // because babel nukes the imports
    return new Function("src", "return import(src);")(source) as Promise<any>;
}

windowRpc.registerFn("takeScreenShot", async () => {
    return new Promise<Blob | null>((resolve) => {
        const el = document.getElementById("appGoesHere");
        if (!el) return null;
        const width = 300 * 2;
        const height = 170 * 2;
        // @ts-expect-error - webpack magic - we would use import()
        // but import gets nuked by babel with modules: "commonjs"
        require.ensure(
            [],
            (require: any) => {
                const h2c = require("html2canvas");
                (h2c as typeof html2canvas)(el, {
                    x: -10,
                    y: -10,
                    width,
                    height,
                    windowWidth: width - 20,
                    windowHeight: height - 20,
                }).then((c) => {
                    c.toBlob((b) => resolve(b));
                });
            },
            "html2CanvasChunk"
        );
    });
});

windowRpc.registerFn("scrollIntoView", (object) => {
    switch (object.type) {
        case "component":
            const component = editState.componentsByName.get(object.data.name);
            if (!component) return;
            component.anvil$hooks.domElement?.scrollIntoView();
            break;
        case "slot":
            const slot = editState.slotsByName.get(object.data.name);
            if (!slot) return;
            slot.element.scrollIntoView();
            break;
        case "section":
            const section = editState.componentsByName.get(object.data.name);
            if (!section) return;
            section.anvil$hooks.getSectionDomElement?.(object.data.id)?.scrollIntoView();
            break;
        default:
            break;
    }
});
